import { ShownField } from './parseInteractionLog';
import { ResolvedInteractionFile } from './useInteractionAttachments';
import { ViewerImage } from '../AttachmentsPanel/ImageViewer';

/** What one answer row actually shows, once file references have been resolved. */
export type AnswerRowValue =
  | { kind: 'text'; text: string }
  | {
      kind: 'image';
      src: string;
      alt: string;
      /**
       * The attachment's id, absent for a signature — which has no attachment, only
       * inline image data. The mobile viewer uses it to offer a download.
       */
      attachmentId?: number;
    }
  | { kind: 'link'; url: string; fileName: string };

export interface AnswerRow {
  field: ShownField;
  value: AnswerRowValue;
}

/** One image in an interaction's browsable set. */
export type AnswerImage = ViewerImage & { attachmentId?: number };

export type ResolveInteractionFile = (
  file: Extract<ShownField['display'], { kind: 'file' }>
) => ResolvedInteractionFile | undefined;

/**
 * Turn one field's display value into a row value.
 *
 * A signature already carries its own image data. An upload carries only a
 * reference, so it becomes an image (browsable, like the signature) when it resolves
 * to an image attachment, a link when it resolves to some other file type, and plain
 * text when it resolves to nothing at all — an attachment removed from the job must
 * still leave a legible history entry rather than a dead thumbnail.
 */
const toRowValue = (field: ShownField, resolve: ResolveInteractionFile): AnswerRowValue => {
  if (field.display.kind === 'image') {
    return { kind: 'image', src: field.display.src, alt: field.display.alt };
  }
  if (field.display.kind === 'text') return { kind: 'text', text: field.display.text };

  const resolved = resolve(field.display);
  if (!resolved) return { kind: 'text', text: field.display.fileName };
  return resolved.isImage
    ? {
        kind: 'image',
        src: resolved.url,
        alt: field.label || resolved.fileName,
        attachmentId: resolved.id,
      }
    : { kind: 'link', url: resolved.url, fileName: resolved.fileName };
};

/**
 * The rows of one interaction, plus every image among them as a single browsable set.
 *
 * Both halves matter and they have to agree: the set is built in row order, so a
 * thumbnail's position in the entry is its position in the viewer. Desktop and mobile
 * share this so an answer is classified the same way on both, and only the markup and
 * the viewer differ.
 */
export const toAnswerRows = (
  fields: ShownField[],
  resolve: ResolveInteractionFile
): { rows: AnswerRow[]; images: AnswerImage[] } => {
  const rows = fields.map((field) => ({ field, value: toRowValue(field, resolve) }));
  const images = rows.flatMap(({ field, value }) =>
    value.kind === 'image'
      ? [
          {
            url: value.src,
            fileName: field.label || value.alt,
            attachmentId: value.attachmentId,
          },
        ]
      : []
  );
  return { rows, images };
};
